<template>
    <main
        :class="$style.page"
        data-interface-section="steam-games"
    >
        <div :class="$style.intro">
            <Typography
                is="h1"
                type="header"
                size="s"
                responsive
            >
                {{ $tr('steamGames.title') }}
            </Typography>
            <Typography
                is="p"
                type="paragraph"
                size="xs"
                :responsive="false"
                :class="$style.subtitle"
            >
                {{ $tr('steamGames.subtitle') }}
            </Typography>
        </div>

        <div :class="$style.games">
            <SteamGameCard
                v-for="(game, index) in STEAM_GAMES"
                :key="game.id"
                :game="game"
                :description="descriptions[game.id]"
                :priority="index === 0"
                :index
            />
        </div>

        <SeoText
            v-if="!appStore.isAppEnv"
            :content="$tr('steamGames.seo')"
            :class="$style.seo_override"
        />

        <LazyHydration :hydrate="hydrateOnIdle()">
            <div
                v-if="trendingGames.length > 0"
                :class="$style.trending"
                data-interface-section="trending-embed"
            >
                <ShelfTitle
                    :title="trendingCategory.title"
                    :link="trendingCategory.location"
                    categoryId="trending_now"
                />
                <GeneralShelf
                    :games="trendingGames.slice(0, 9)"
                    :link="trendingCategory.location"
                    :gameLinkTarget="undefined"
                    embedded
                    :categoryName="trendingCategory.title"
                />
            </div>
        </LazyHydration>

        <LazyHydration :hydrate="hydrateOnIdle()">
            <PromoCategories />
        </LazyHydration>

        <TheEnd />
    </main>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { computed, hydrateOnIdle, onServerPrefetch } from 'vue'

import GeneralShelf from '@/components/GamesShelf/GeneralShelf.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import SeoText from '@/components/SeoText/SeoText.vue'
import TheEnd from '@/components/TheEnd/TheEnd.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { useTrendingData } from '@/query/use-trending-data'
import { useAppStore } from '@/store/app-store'

import SteamGameCard from './components/SteamGameCard.vue'
import { STEAM_GAMES, SteamGameId } from './games'

const appStore = useAppStore()
const tr = useTr()

const {
    currentTrending: trendingCategory,
    unzippedTrendingWithGames: trendingGames,
    preloadTrendingData,
} = useTrendingData()

const descriptions = computed<Record<SteamGameId, string>>(() => ({
    'world-of-warships': tr('steamGames.games.worldOfWarships.description'),
    'escape-from-tarkov': tr('steamGames.games.escapeFromTarkov.description'),
}))

const metaTitle = computed(() => tr('steamGames.metaTitle'))
const metaDescription = computed(() => tr('steamGames.metaDescription'))

const pageMeta = computed(() => ({
    title: () => metaTitle.value,
    meta: [
        { name: 'robots', content: 'noindex, nofollow' },
        { name: 'title', content: metaTitle.value },
        { name: 'description', content: metaDescription.value },
        { property: 'og:title', content: metaTitle.value },
        { property: 'og:description', content: metaDescription.value },
        { name: 'twitter:title', content: metaTitle.value },
        { name: 'twitter:description', content: metaDescription.value },
    ],
}))

useHead(pageMeta)

onServerPrefetch(() => preloadTrendingData().catch(() => {}))
</script>

<style module>
.page {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
}

.intro {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-inline: 6px;
}

.subtitle {
    color: var(--white-60);
}

.games {
    padding-inline: 6px;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.seo_override {
    width: auto;
    margin-inline: 6px;
    padding: 16px;
    border-radius: var(--radius-16);
    background: var(--experimental-purple-graphit);
    box-sizing: border-box;
}

.seo_override > div {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.seo_override h2 {
    margin: 0;
    font-size: var(--font-size-header-s);
    line-height: 1.2;
    color: var(--white);
}

.trending {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (--desktop-small-plus) {
    .page {
        row-gap: 32px;
    }

    .subtitle {
        font-size: var(--font-size-paragraph-m);
        line-height: var(--line-height-paragraph-m);
    }

    .intro {
        padding-inline: 0;
    }

    .games {
        padding-inline: 0;
        gap: 40px;
    }

    .seo_override {
        width: 100%;
        margin-inline: 0;
        padding: 32px;
        border-radius: var(--radius-24);
    }
}

@media (--desktop-large-plus) {
    .subtitle {
        font-size: var(--font-size-paragraph-l);
        line-height: var(--line-height-paragraph-l);
    }
}
</style>
